import { BulletList, Screenshot, ScreenshotGrid } from '../components/UIComponents';
import { Settings, Search, RadioTower, Share2, Activity, Cpu, Network } from 'lucide-react';

export function AgentGatewayDeployment() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="agent-gateway">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Settings className="text-emerald-500" size={22} />
                    4. Agent Gateway Deployment
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    The Agent Gateway is your main on-site tool. Once deployed on your network, it acts as the bridge connecting your local infrastructure to the central monitoring server.
                </p>
            </div>
            <div className="space-y-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Physical Deployment</h3>
                    <p className="text-[13px] text-gray-200 leading-relaxed">
                        You simply plug the Agent Gateway hardware into your local network, and it handles the rest. It grabs its network settings securely via DHCP or a static IP and starts discovering devices immediately. There is no need to make changes to your existing network.
                    </p>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Polling Schedule Configuration</h3>
                    <div className="overflow-x-auto rounded-lg border border-white/10">
                        <table className="w-full text-left border-collapse text-[12px]">
                            <thead className="bg-gray-900">
                                <tr>
                                    <th className="px-4 py-2 border-b border-white/10">Category</th>
                                    <th className="px-4 py-2 border-b border-white/10">Default</th>
                                </tr>
                            </thead>
                            <tbody>
                                {['Network Switches (5 minutes / 1 min — 24 hours)', 'Firewalls (5 minutes / 1 min — 24 hours)', 'Wi-Fi Access Points (5 minutes / 1 min — 24 hours)', 'IP Cameras (5 minutes / 1 min — 24 hours)', 'Servers iDRAC / iLO (10 minutes / 1 min — 24 hours)', 'Endpoint Agents (5 minutes / 1 min — 24 hours)', 'Environmental Sensors (1 minute / 30 sec — 1 hour)', 'Network Discovery Scan (1 hour / 15 min — 24 hours)'].map((item, i) => (
                                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                                        <td className="px-4 py-2 text-gray-300">{item.split(' (')[0]}</td>
                                        <td className="px-4 py-2 text-gray-400">{item.split(' (')[1].slice(0, -1)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function DiscoveryAssetManagement() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="discovery-asset">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Search className="text-amber-500" size={22} />
                    5. Network Discovery & Asset Management
                </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-2">Discovery Protocols</h3>
                    <BulletList items={[
                        'SNMP v1/v2c/v3 — Network devices & MIB data',
                        'ICMP — Reachability and latency',
                        'ARP — IP-to-MAC mapping',
                        'LLDP / CDP — Physical port topology',
                        'NetBIOS — Windows host discovery and hostname resolution',
                        'Bonjour / mDNS — Apple, IoT, and service-broadcasting device discovery',
                        'UPnP — Universal Plug and Play device detection',
                        'ONVIF — IP camera discovery',
                        'WS-Discovery — Windows endpoint discovery',
                        'NMAP / Port Scan — Open ports and OS fingerprinting',
                        'WMI — Deep Windows endpoint data',
                        'SSH / Telnet — CLI-based interrogation where policy permits',
                        'IPMI — Server out-of-band access'
                    ]} />
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-2">Inventory Capabilities</h3>
                    <BulletList items={[
                        'Auto-Classification: We automatically group devices by type when we spot them.',
                        'L2/L3 Tracking: Easily identify subnets and see where VLANs belong.',
                        'Auto-Assignment: Devices automatically get the right monitoring profiles.',
                        'Change Detection: Get alerted right away if a new, unexpected device appears.',
                        'Continuous Updates: Keep your inventory accurate with recurring scheduled scans.',
                        'Asset Tags: Filter and report easily by adding custom labels to your devices.'
                    ]} />
                    <Screenshot
                        src="/Inventory_Capabilities.png"
                        alt="Inventory Capabilities"
                    />
                </div>
            </div>
        </section>
    );
}

export function SNMPMonitoring() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="snmp-overview">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <RadioTower className="text-orange-400" size={22} />
                    6. SNMP Monitoring & Credentials
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    SNMP is a major way we monitor your devices. We use it to securely discover what's out there, check device health frequently, and build a clearer picture of your network.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">What SNMP Covers</h3>
                    <BulletList items={[
                        'SNMP handles communications with switches, routers, firewalls, IP cameras, and wireless devices.',
                        'It helps us get device names, see what interfaces are active, and read traffic counters.',
                        'Because SNMP gives us detailed information, it makes our topology and port-level mapping much more accurate.',
                        'We cross-check SNMP data with standard pings and MAC tables so you never miss an update.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Supported SNMP Models</h3>
                    <BulletList items={[
                        'We support SNMP v1, v2c, and v3, handling multiple versions at once on the same network.',
                        'Older devices can use SNMP v1/v2c with community strings.',
                        'Newer devices can securely use SNMP v3 with full authentication and encryption features.',
                        'You don\'t have to force every device to use the same SNMP setup—we work with your current standards.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Credential Context</h3>
                    <BulletList items={[
                        'Your credentials allow the gateway to run recurring polls on authorized devices securely.',
                        'Using credentials enables us to pull much more detail than a basic ping could offer.',
                        'With good setup here, your dashboards, topology maps, and alert triggers will be far more reliable.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Operational Value</h3>
                    <BulletList items={[
                        'SNMP provides the critical numbers you need: bandwidth, port health, error rates, and CPU data.',
                        'This helps you quickly spot anomalies, trigger useful alerts, and track historical trends.',
                        'If something breaks, proper SNMP monitoring lets you find out exactly which device is causing the problem.'
                    ]} />
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/SNMP_Credential_Configuration_Prerequisites.png"
                    alt="SNMP Credential Configuration Overview"
                />
                <Screenshot
                    src="/Configure_SNMP_Credentials.png"
                    alt="SNMP Credential Profile"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function NetworkDeviceMonitoring() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="network-device">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <RadioTower className="text-orange-400" size={22} />
                    7. Network Device Monitoring
                </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                    { icon: <Activity size={14} />, title: 'Uptime Tracking' },
                    { icon: <Cpu size={14} />, title: 'Firmware Detection' },
                    { icon: <Network size={14} />, title: 'Port Status & PoE' },
                    { icon: <Activity size={14} />, title: 'RX/TX Errors' },
                    { icon: <Network size={14} />, title: 'VLAN Visibility' },
                    { icon: <Search size={14} />, title: 'MAC Table Analysis' },
                    { icon: <Activity size={14} />, title: 'ICMP Loss, RTT & Jitter' },
                    { icon: <Network size={14} />, title: 'CDP/LLDP Neighbors' },
                    { icon: <Search size={14} />, title: 'System Logs & Open Ports' }
                ].map(item => (
                    <div key={item.title} className="bg-gray-800 border border-white/5 rounded-lg p-3 hover:border-orange-500/30 transition-all">
                        <div className="text-[13px] font-bold text-gray-200 flex items-center gap-2">
                            <span className="text-orange-400">{item.icon}</span> {item.title}
                        </div>
                    </div>
                ))}
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5 mt-4">
                <h3 className="text-[15px] font-bold text-gray-100 mb-3">Firewall Monitoring</h3>
                <BulletList items={[
                    'Keep tabs on basics like uptime, CPU usage, memory, interfaces, VPN tunnels, NAT, and traffic usage.',
                    'Monitor security features directly: review rules, object groups, certificates, web filters, and check automated compliance scans.'
                ]} />
            </div>
            <Screenshot
                src="/Network_Device_Monitoring.png"
                alt="Network Device Monitoring"
            />
        </section>
    );
}

export function TopologyVisualization() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="topology-viz">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Share2 className="text-indigo-400" size={22} />
                    8. Automated Network Topology
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    Using combinations of SNMP, CDP, LLDP, and MAC information, OvulScanner builds and updates a map of your network so you always know how everything connects.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Topology Map Capabilities</h3>
                    <BulletList items={[
                        'See every switch, router, AP, and firewall directly on your map.',
                        'Trace the physical connections and see how your cables actually run.',
                        'Watch exactly which switch port your cameras, servers, and computers are plugged into.',
                        'Click any device to check its health, how many ports are free, and see overall usage.',
                        'Spot outages easily: offline devices turn red, warnings show yellow, healthy items stay green.',
                        'If someone unplugs a cable, the map refreshes itself to reflect the new state immediately.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Dependency Mapping</h3>
                    <BulletList items={[
                        'We constantly keep track of which devices rely on others.',
                        'If a piece of equipment goes offline, you can quickly see which firewall or switch was in charge of it.',
                        'Understand the whole impact clearly—we highlight every camera or endpoint affected when a switch drops.',
                        'This dramatically speeds up troubleshooting because you won\'t have to investigate each device individually.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5 md:col-span-2">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">Path Tracing & Debugging</h3>
                    <BulletList items={[
                        'Follow the route your data takes between any two devices on the network.',
                        'Visually spot where a failure is happening and who it takes down.',
                        'Determine exactly which port or cable is at fault.',
                        'Easily confirm your fix works—the map will turn back to green without reloading.'
                    ]} />
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/AI-Powered_Network_Topology.png"
                    alt="AI-Powered Network Topology"
                />
                <Screenshot
                    src="/AI-Powered_Network_Topology1.png"
                    alt="AI-Powered Network Topology Detail"
                />
            </ScreenshotGrid>
        </section>
    );
}
